import type { CompareScenario, CompatibilityOutcome, CompatibilityVerdict, ComparisonCheck } from 'fuel-benchmark-contracts';
import type { TFunction } from 'i18next';
import type { StatusTone } from '../../components/ui/StatusText';
import { formatDecimal } from '../../lib/format';
import { runConditionsSummary } from '../runs/run-display';

/**
 * How a comparison *reads*: tones, one-line summaries and the strings a fact table prints. The rules about
 * which vehicles and runs a comparison spans live in `compare-selection.ts`, which needs no translator.
 */

/**
 * Colour discipline (plan §2.2): the verdict is the one thing on this screen that has to catch the eye.
 * A comparison that holds up stays green, a warning is amber, and a blocked one is red.
 */
export const verdictTone = (verdict: CompatibilityVerdict): StatusTone => {
  switch (verdict) {
    case 'COMPARABLE':
      return 'success';
    case 'COMPARABLE_WITH_WARNING':
      return 'warning';
    case 'NOT_COMPARABLE':
      return 'danger';
  }
};

export const compatibilityOutcomeTone = (outcome: CompatibilityOutcome): StatusTone => {
  switch (outcome) {
    case 'PASS':
      return 'success';
    case 'WARN':
      return 'warning';
    case 'BLOCK':
      return 'danger';
  }
};

/** `Summer · Day · A/C on · Mixed` — the same one-line summary the runs list and coverage matrix use. */
export const scenarioSummary = (scenario: CompareScenario, t: TFunction): string => runConditionsSummary({ scenario }, t);

const withUnit = (value: string, unit: string | null): string => (unit ? `${value} ${unit}` : value);

/** What one vehicle recorded for a check — `95 RON`, `38 C`, or an em dash when nothing was measured. */
export const checkValuesOf = (check: ComparisonCheck, vehicleId: string): string => {
  const entry = check.detail.perVehicle.find((candidate) => candidate.vehicleId === vehicleId);

  if (!entry || entry.distinctValues.length === 0) {
    return '—';
  }

  return entry.distinctValues.map((value) => withUnit(value, check.detail.unit)).join(', ');
};

/** The spread a `max_diff` check measured, against the tolerance it was judged by. */
export const checkSpreadOf = (check: ComparisonCheck): string => {
  if (check.detail.threshold === null) {
    return '—';
  }

  const spread = check.detail.spread === null ? '—' : formatDecimal(check.detail.spread, 2);

  return `${withUnit(spread, check.detail.unit)} / ≤ ${withUnit(formatDecimal(check.detail.threshold, 2), check.detail.unit)}`;
};
